const MOBILE_MEDIA = '(max-width: 1349px)';
const SCROLL_LOCK_CLASS = 'is-scroll-locked';

/**
 * Le panneau mobile couvre l'écran : le document ne doit plus défiler derrière.
 * Lenis étant limité aux pointeurs fins, c'est bien le scroll natif qu'on bloque.
 */
function lockScroll(locked) {
    const root = document.documentElement;

    if (locked) {
        // Sans compensation, masquer la scrollbar élargit le viewport
        // et décale le header au moment de l'ouverture.
        const scrollbar = window.innerWidth - root.clientWidth;
        root.style.setProperty('--scrollbar-width', `${scrollbar}px`);
    }

    document.body.classList.toggle(SCROLL_LOCK_CLASS, locked);

    if (!locked) {
        root.style.removeProperty('--scrollbar-width');
    }
}

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

function isFocusable(element) {
    if (!(element instanceof HTMLElement) || element.tabIndex < 0) return false;
    if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;

    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
}

function Navigation(root) {
    const nav = root.querySelector('#nav');
    const panel = root.querySelector('#nav-panel');
    const button = root.querySelector('.btn-nav');

    if (!button || !nav || !panel) return;

    const mobileMedia = window.matchMedia(MOBILE_MEDIA);
    let transitionTimer = 0;
    let focusAttempt = 0;
    let trapController = null;

    const isTrapActive = () => root.classList.contains('is-open') && mobileMedia.matches;

    const panelFocusables = () => [...panel.querySelectorAll(FOCUSABLE_SELECTOR)].filter(element => {
        if (!(element instanceof HTMLElement)) return false;
        if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;

        const style = window.getComputedStyle(element);
        if (style.display === 'none') return false;

        // Panneau ouvert : ne pas exclure les liens pendant la transition
        // (visibility encore héritée sur un frame).
        if (!isTrapActive() && style.visibility === 'hidden') return false;

        return true;
    });

    const tabTrap = () => [button, ...panelFocusables()];

    /** Hors bouton + panneau : logo, main, footer, etc. */
    const inertRoots = () => [
        document.querySelector('.skip-links'),
        root.querySelector('.header-nav-logo'),
        root.querySelector('.header-nav-buttons'),
        document.getElementById('main-content'),
        document.querySelector('footer[role="contentinfo"], footer'),
    ].filter(element => element instanceof HTMLElement);

    const setPageInert = inert => {
        inertRoots().forEach(element => {
            if (inert) element.setAttribute('inert', '');
            else element.removeAttribute('inert');
        });
    };

    const keepFocusInTrap = target => {
        if (tabTrap().includes(target)) return;
        button.focus({ preventScroll: true });
    };

    const focusTrapIndex = (index) => {
        const trap = tabTrap();
        if (trap.length === 0) return;

        const normalized = (index + trap.length) % trap.length;
        trap[normalized].focus({ preventScroll: true });
    };

    const focusFirstItem = () => {
        const attemptId = ++focusAttempt;
        let frames = 0;

        const attempt = () => {
            if (attemptId !== focusAttempt || !root.classList.contains('is-open')) return;

            frames += 1;
            const target = panelFocusables()[0];

            // Le panneau passe de visibility:hidden à visible via une transition :
            // au premier frame les liens sont encore ignorés, et le clic laisse
            // le focus sur le bouton. On réessaie jusqu'à ce que le premier lien
            // prenne vraiment le focus.
            if (target) {
                target.focus({ preventScroll: true });
                panel.scrollTop = 0;
                if (document.activeElement === target) return;
            }

            if (frames < 8) window.requestAnimationFrame(attempt);
        };

        window.requestAnimationFrame(attempt);
    };

    const setOpen = (open, animate = true) => {
        clearTimeout(transitionTimer);
        root.classList.toggle('is-nav-animating', animate);

        if (animate) {
            transitionTimer = window.setTimeout(() => {
                root.classList.remove('is-nav-animating');
                transitionTimer = 0;
            }, 300);
        }

        root.classList.toggle('is-open', open);
        button.setAttribute('aria-expanded', String(open));
        panel.toggleAttribute('data-lenis-prevent', open);
        lockScroll(open);
        trapController?.abort();
        trapController = null;

        if (open && mobileMedia.matches) {
            trapController = new AbortController();
            const { signal } = trapController;

            setPageInert(true);

            document.addEventListener('keydown', event => {
                if (!isTrapActive()) return;

                if (event.key === 'Escape') {
                    event.preventDefault();
                    closeAndRestoreFocus();
                    return;
                }

                if (event.key !== 'Tab') return;

                const trap = tabTrap();
                event.preventDefault();

                if (trap.length === 0) return;

                const activeIndex = trap.indexOf(document.activeElement);
                const step = event.shiftKey ? -1 : 1;
                const nextIndex = activeIndex === -1
                    ? (event.shiftKey ? trap.length - 1 : 0)
                    : activeIndex + step;

                focusTrapIndex(nextIndex);
            }, { signal, capture: true });

            document.addEventListener('focusin', event => {
                if (!isTrapActive() || !(event.target instanceof Element)) return;
                keepFocusInTrap(event.target);
            }, { signal, capture: true });

            focusFirstItem();
        } else {
            setPageInert(false);
            focusAttempt += 1;
        }
    };

    const closeAndRestoreFocus = () => {
        setOpen(false);
        button.focus();
    };

    button.addEventListener('click', () => {
        const open = !root.classList.contains('is-open');
        setOpen(open);
    });

    panel.addEventListener('click', (event) => {
        if (event.target instanceof Element && event.target.closest('a')) {
            setOpen(false);
        }
    });

    const updateViewport = (matches) => {
        if (!matches) setOpen(false, false);
    };

    const onViewportChange = (event) => {
        updateViewport(event.matches);
    };

    if (mobileMedia.addEventListener) {
        mobileMedia.addEventListener('change', onViewportChange);
    } else {
        mobileMedia.addListener(onViewportChange);
    }

    updateViewport(mobileMedia.matches);
}

function HeaderScroll(root, {
    hideOnScroll = false,
    compactOnScroll = false,
    threshold = 0,
} = {}) {
    const scrollThreshold = Math.max(0, Number(threshold) || 0);
    let scrollFrame = 0;
    let previousScrollY = Math.max(0, window.scrollY);

    const update = () => {
        const scrollY = Math.max(0, window.scrollY);
        scrollFrame = 0;
        root.classList.toggle(
            'is-compact',
            compactOnScroll && scrollY > scrollThreshold,
        );

        if (
            !hideOnScroll
            || scrollY <= scrollThreshold
            || root.classList.contains('is-open')
            || root.contains(document.activeElement)
        ) {
            root.classList.remove('is-hidden');
        } else if (scrollY > previousScrollY) {
            root.classList.add('is-hidden');
        } else if (scrollY < previousScrollY) {
            root.classList.remove('is-hidden');
        }

        previousScrollY = scrollY;
    };

    const onScroll = () => {
        if (scrollFrame) return;
        scrollFrame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    root.addEventListener('focusin', () => {
        root.classList.remove('is-hidden');
    });
    update();

    const enableTransitions = () => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                root.classList.add('is-scroll-ready');
            });
        });
    };

    if (document.readyState === 'complete') {
        enableTransitions();
    } else {
        window.addEventListener('pageshow', enableTransitions, { once: true });
    }
}

export default function HeaderNav() {
    const headerNav = document.getElementById("header-nav");

    if (!headerNav) return;

    Navigation(headerNav);
    HeaderScroll(headerNav, {
        hideOnScroll: false,
        compactOnScroll: true,
        threshold: 100,
    });
}
